// Envelope detail — status, signers, audit trail, download completed artifacts.
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Copy, Download, ShieldCheck, ShieldAlert, XCircle,
  CheckCircle2, Clock, Eye, FileSignature, Ban, ExternalLink, Trash2,
} from "lucide-react";
import api from "./api";

const STATUS_META = {
  draft:       { label: "Draft",        color: "#8e8e93" },
  sent:        { label: "Sent",         color: "#0071e3" },
  in_progress: { label: "In progress",  color: "#ff9f0a" },
  completed:   { label: "Completed",    color: "#30d158" },
  declined:    { label: "Declined",     color: "#ff453a" },
  voided:      { label: "Voided",       color: "#636366" },
};

export default function SignEnvelope() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [verify, setVerify] = useState(null);
  const [copied, setCopied] = useState("");

  const load = () => api.get(`/sign/envelopes/${id}`).then((r) => setData(r.data));
  useEffect(() => { load(); }, [id]);

  useEffect(() => {
    if (data?.envelope?.status === "completed") {
      api.get(`/sign/verify/${id}`).then((r) => setVerify(r.data)).catch(() => {});
    }
  }, [data?.envelope?.status, id]);

  if (!data) return <div className="text-[13px]" style={{ color: "var(--crm-text-muted)" }}>Loading envelope…</div>;
  const { envelope, signers, fields, events } = data;
  const meta = STATUS_META[envelope.status] || STATUS_META.draft;

  const signingUrl = (token) => `${window.location.origin}/sign/${token}`;
  const copy = (val, tag) => {
    navigator.clipboard.writeText(val);
    setCopied(tag);
    setTimeout(() => setCopied(""), 1500);
  };
  const downloadArtifact = async (kind) => {
    const { data } = await api.get(`/sign/envelopes/${id}/download/${kind}`);
    const bin = atob(data.doc_b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    const blob = new Blob([bytes], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = data.doc_name; a.click();
    URL.revokeObjectURL(url);
  };

  const doVoid = async () => {
    const reason = prompt("Reason for voiding (optional):");
    if (reason === null) return;
    await api.post(`/sign/envelopes/${id}/void`, { reason });
    load();
  };

  const doDelete = async () => {
    if (!window.confirm("Delete this envelope permanently? This cannot be undone.")) return;
    await api.delete(`/sign/envelopes/${id}`);
    nav("/crm/sign");
  };

  return (
    <div data-testid="sign-envelope-page" className="space-y-6">
      <button onClick={() => nav("/crm/sign")} className="inline-flex items-center gap-1 text-[13px]"
        style={{ color: "var(--crm-text-muted)" }} data-testid="back-to-envelopes">
        <ArrowLeft size={14} /> All envelopes
      </button>

      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-[28px] tracking-tight leading-none"
            style={{ color: "var(--crm-text)", fontWeight: 600, letterSpacing: "-0.02em" }}>
            {envelope.subject}
          </h1>
          <div className="mt-2 text-[13px]" style={{ color: "var(--crm-text-muted)" }}>
            {envelope.doc_name}
            <span className="mx-2">·</span>
            <span className="font-mono text-[11.5px]">{envelope.doc_hash?.slice(0, 16)}…</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px]"
            style={{ background: `${meta.color}15`, color: meta.color, border: `1px solid ${meta.color}30` }}
            data-testid="envelope-status">
            {envelope.status === "completed" ? <CheckCircle2 size={12} /> :
             envelope.status === "declined" || envelope.status === "voided" ? <XCircle size={12} /> :
             <Clock size={12} />} {meta.label}
          </span>
          {envelope.status !== "completed" && envelope.status !== "voided" && (
            <button onClick={doVoid} data-testid="void-envelope"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px]"
              style={{ background: "var(--crm-hover)", color: "#ff453a" }}>
              <Ban size={12} /> Void
            </button>
          )}
          <button onClick={doDelete} data-testid="delete-envelope"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px]"
            style={{ background: "var(--crm-hover)", color: "#ff453a" }}>
            <Trash2 size={12} /> Delete
          </button>
        </div>
      </header>

      {/* Verification banner */}
      {verify && (
        <div className="rounded-2xl p-4 flex items-start gap-3"
          style={{
            background: verify.chain_ok && verify.server_signature_valid
              ? "rgba(48,209,88,0.06)" : "rgba(255,69,58,0.06)",
            border: `1px solid ${verify.chain_ok && verify.server_signature_valid ? "rgba(48,209,88,0.25)" : "rgba(255,69,58,0.25)"}`,
          }}
          data-testid="verify-banner">
          {verify.chain_ok && verify.server_signature_valid ? (
            <ShieldCheck size={18} style={{ color: "#30d158" }} />
          ) : (
            <ShieldAlert size={18} style={{ color: "#ff453a" }} />
          )}
          <div className="flex-1 text-[12.5px]">
            <div style={{ color: "var(--crm-text)", fontWeight: 600 }}>
              {verify.chain_ok && verify.server_signature_valid
                ? "Envelope integrity verified"
                : "Verification failed — chain or signature invalid"}
            </div>
            <div className="mt-1 font-mono text-[11px]" style={{ color: "var(--crm-text-muted)" }}>
              chain root: {verify.chain_root}
            </div>
            <div className="font-mono text-[11px]" style={{ color: "var(--crm-text-muted)" }}>
              Ed25519 sig: {verify.server_signature_hex?.slice(0, 32)}…{verify.server_signature_hex?.slice(-16)}
            </div>
            <div className="mt-1 text-[11px]" style={{ color: "var(--crm-text-subtle)" }}>
              {verify.event_count} events chained · public key fp {verify.public_key.fingerprint.slice(0, 12)}…
            </div>
          </div>
          {envelope.status === "completed" && (
            <div className="flex gap-2">
              <button onClick={() => downloadArtifact("stamped")} data-testid="download-stamped"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] text-black"
                style={{ background: "linear-gradient(180deg, #f4d47b 0%, #d4a53a 55%, #b78a2f 100%)", fontWeight: 500 }}>
                <Download size={12} /> Signed PDF
              </button>
              <button onClick={() => downloadArtifact("certificate")} data-testid="download-cert"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px]"
                style={{ background: "var(--crm-bg-elev)", border: "1px solid var(--crm-line)", color: "var(--crm-text)" }}>
                <Download size={12} /> Certificate
              </button>
            </div>
          )}
        </div>
      )}

      {/* Signers */}
      <section>
        <h2 className="text-[13px] uppercase tracking-widest mb-3" style={{ color: "var(--crm-text-subtle)" }}>Signers</h2>
        <div className="space-y-3">
          {signers.map((s, i) => (
            <div key={s.id} className="rounded-2xl p-4 flex items-center gap-4"
              style={{ background: "var(--crm-glass)", border: "1px solid var(--crm-line)" }}
              data-testid={`signer-card-${s.id}`}>
              <div className="w-9 h-9 rounded-full flex items-center justify-center text-[12px]"
                style={{ background: "var(--crm-accent-soft)", color: "var(--crm-accent)", fontWeight: 600 }}>
                {i + 1}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 text-[14px]" style={{ color: "var(--crm-text)" }}>
                  <span style={{ fontWeight: 500 }}>{s.name}</span>
                  <span className="text-[11.5px] px-1.5 py-0.5 rounded"
                    style={{ background: "var(--crm-hover)", color: "var(--crm-text-muted)" }}>{s.role}</span>
                </div>
                <div className="text-[12px]" style={{ color: "var(--crm-text-muted)" }}>{s.email}</div>
                {s.status === "signed" && s.ip && (
                  <div className="text-[10.5px] font-mono mt-1" style={{ color: "var(--crm-text-subtle)" }}>
                    signed {new Date(s.signed_at).toLocaleString("en-CA")} · {s.ip}{s.geo_country ? ` (${s.geo_country})` : ""}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <SigStatusPill status={s.status} />
                {s.status === "pending" && envelope.status !== "voided" && envelope.status !== "completed" && (
                  <>
                    <button onClick={() => copy(signingUrl(s.token), `link-${s.id}`)}
                      data-testid={`copy-link-${s.id}`}
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[11.5px]"
                      style={{ background: "var(--crm-bg-elev)", border: "1px solid var(--crm-line)", color: "var(--crm-text)" }}>
                      <Copy size={11} /> {copied === `link-${s.id}` ? "Copied!" : "Copy link"}
                    </button>
                    <a href={signingUrl(s.token)} target="_blank" rel="noopener noreferrer"
                      data-testid={`open-link-${s.id}`}
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[11.5px]"
                      style={{ background: "var(--crm-bg-elev)", border: "1px solid var(--crm-line)", color: "var(--crm-accent)" }}>
                      <ExternalLink size={11} /> Open
                    </a>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Audit trail */}
      <section>
        <h2 className="text-[13px] uppercase tracking-widest mb-3" style={{ color: "var(--crm-text-subtle)" }}>Audit trail</h2>
        <div className="rounded-2xl overflow-hidden"
          style={{ background: "var(--crm-glass)", border: "1px solid var(--crm-line)" }}>
          <table className="w-full text-[12.5px]">
            <thead>
              <tr style={{ borderBottom: "1px solid var(--crm-line-soft)" }}>
                <Th className="w-10">#</Th>
                <Th>Event</Th>
                <Th>Actor</Th>
                <Th className="hidden md:table-cell">When</Th>
                <Th className="hidden lg:table-cell">Event hash</Th>
              </tr>
            </thead>
            <tbody>
              {events.map((ev) => (
                <tr key={ev.id} style={{ borderBottom: "1px solid var(--crm-line-soft)" }}
                  data-testid={`event-row-${ev.seq}`}>
                  <Td className="tabular-nums" style={{ color: "var(--crm-text-subtle)" }}>{ev.seq}</Td>
                  <Td style={{ color: "var(--crm-text)", fontWeight: 500 }}>{ev.type}</Td>
                  <Td style={{ color: "var(--crm-text-muted)" }}>{ev.actor}</Td>
                  <Td className="hidden md:table-cell" style={{ color: "var(--crm-text-muted)" }}>
                    {new Date(ev.created_at).toLocaleString("en-CA")}
                  </Td>
                  <Td className="hidden lg:table-cell font-mono text-[11px]" style={{ color: "var(--crm-text-subtle)" }}>
                    {ev.event_hash?.slice(0, 24)}…
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Fields summary */}
      <section>
        <h2 className="text-[13px] uppercase tracking-widest mb-3" style={{ color: "var(--crm-text-subtle)" }}>
          Fields ({fields.length})
        </h2>
        <div className="rounded-2xl p-4 text-[12px]"
          style={{ background: "var(--crm-glass)", border: "1px solid var(--crm-line)", color: "var(--crm-text-muted)" }}>
          {fields.length} fields across {Math.max(1, new Set(fields.map((f) => f.page)).size)} page(s).
        </div>
      </section>
    </div>
  );
}

function SigStatusPill({ status }) {
  const c = status === "signed" ? "#30d158" : status === "declined" ? "#ff453a" : "#ff9f0a";
  const L = status === "signed" ? "Signed" : status === "declined" ? "Declined" : "Waiting";
  return (
    <span className="inline-flex items-center gap-1 text-[11.5px] px-2 py-1 rounded-full"
      style={{ background: `${c}15`, color: c, border: `1px solid ${c}30` }}>
      {status === "signed" ? <CheckCircle2 size={10} /> : <Clock size={10} />} {L}
    </span>
  );
}

const Th = ({ children, className = "" }) => (
  <th className={`px-4 py-2.5 text-left text-[10.5px] uppercase tracking-[0.08em] font-normal ${className}`}
    style={{ color: "var(--crm-text-subtle)" }}>{children}</th>
);
const Td = ({ children, className = "", ...rest }) => (
  <td className={`px-4 py-2.5 align-middle ${className}`} {...rest}>{children}</td>
);
